import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Store } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ACCESS_TOKEN_KEY } from "@/lib/constants";
import { getLaunchPopup, markLaunchPopupSeen } from "@/lib/services/launchPopups";
import { getOnboardingStatus } from "@/lib/services/onboarding";

/** Where the pop-up would get in the way: signing in or up, onboarding, and verifying. */
const QUIET_PATHS = [
  "/login", "/register", "/signup-success", "/activate-account", "/verify-email",
  "/forgot-password", "/reactivate-account", "/onboarding",
  "/employment-verification", "/employment-verify",
];

const isQuiet = (pathname: string) => QUIET_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
const onVendors = (pathname: string) => pathname === "/vendors" || pathname.startsWith("/vendors/");

/**
 * The dialog opens by itself, so Radix has no trigger to give focus back to on
 * close and it would fall to <body>. Start again at the page's content, as a new
 * page does (ScrollManager).
 */
function focusPageContent(event: Event) {
  event.preventDefault();
  const main = document.querySelector("main");
  if (!main) return;
  if (!main.hasAttribute("tabindex")) {
    main.setAttribute("tabindex", "-1");
    main.addEventListener("blur", () => main.removeAttribute("tabindex"), { once: true });
  }
  main.focus({ preventScroll: true });
}

const hasToken = () => {
  try {
    return Boolean(localStorage.getItem(ACCESS_TOKEN_KEY));
  } catch {
    return false;
  }
};

/**
 * Announces Tappanon Vendors once, to every member (founder, 2026-10-19): on
 * their next login, or their next visit if they stayed signed in. It goes before
 * the verification pop-up, which the server holds back for a later visit. The
 * server remembers it across devices. A member who opens Vendors first has found
 * it, so that counts as seen and the pop-up never interrupts them.
 */
export default function VendorsLaunchPopup() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const checkedFor = useRef<string | null>(null);
  const quiet = isQuiet(pathname);
  const quietRef = useRef(quiet);
  quietRef.current = quiet;
  const vendorsRef = useRef(onVendors(pathname));
  vendorsRef.current = onVendors(pathname);
  const member = user?.username ?? null;

  useEffect(() => {
    if (!member || quiet || checkedFor.current === member || !hasToken()) return;
    checkedFor.current = member;
    getLaunchPopup("vendors")
      .then(async ({ show }) => {
        if (!show) return;
        // The server shows it to members still onboarding too; the page keeps it off
        // onboarding. Opening /feed before onboarding, this answer can land before the
        // guard's redirect to /onboarding, so ask rather than race it.
        const { onboarding_completed } = await getOnboardingStatus();
        if (!onboarding_completed || quietRef.current) {
          // Not through onboarding yet, or they moved on to a sign-in or verification page
          // meanwhile: ask again on the next page.
          checkedFor.current = null;
          return;
        }
        // Once means once: recorded as soon as it appears (or they're already on Vendors).
        markLaunchPopupSeen("vendors").catch(() => {});
        if (!vendorsRef.current) setOpen(true);
      })
      .catch(() => {
        checkedFor.current = null;
      });
    // pathname: after a failed check, the next page asks again.
  }, [member, quiet, pathname]);

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  /** A long dialog starts at its top: Radix would focus "Explore Vendors", which at
      large text or on a small phone sits below the fold of a dialog scrolled to the top. */
  function focusTitle(event: Event) {
    event.preventDefault();
    titleRef.current?.focus({ preventScroll: true });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-w-lg max-h-[90dvh] overflow-y-auto"
        onOpenAutoFocus={focusTitle}
        onCloseAutoFocus={focusPageContent}
      >
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <Store className="h-6 w-6 text-brand-dark" aria-hidden />
          </div>
          <DialogTitle ref={titleRef} tabIndex={-1} className="text-center text-xl focus:outline-none">
            Tappanon Vendors has launched
          </DialogTitle>
          <DialogDescription className="text-center text-base text-gray-600">
            Find and recommend vendors that serve HR professionals and businesses, chosen by HR members who've used them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm leading-relaxed text-gray-700">
          <p>
            <span className="font-semibold text-gray-900">Planning your end-of-year party?</span>{" "}
            Find DJs, MCs, caterers, event centres, photographers, gift vendors and more for corporate events,
            staff celebrations and team bonding.
          </p>
          <p>
            Beyond events, there are vendors for HR, health and insurance, payroll, travel, staff welfare and other
            business needs.
          </p>
        </div>

        <div className="rounded-lg bg-gray-50 p-4">
          <h3 className="text-sm font-semibold text-gray-900">How it works</h3>
          <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-gray-700">
            <li>Browse by category or state, and see ratings, reviews and what members paid.</li>
            <li>Hired someone good? Recommend them so the next HR doesn't have to ask around. Your name is never shown.</li>
            <li>Offer an HR service yourself, like consulting, recruitment, training or payroll? You can add it too.</li>
          </ol>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          {/* min-h, not h, and allowed to wrap: at 200% text "Recommend a vendor" no longer
              sets the dialog's minimum width and pushes every line past the screen. */}
          <Button onClick={() => go("/vendors")} className="h-auto min-h-11 w-full whitespace-normal bg-brand-dark font-semibold text-white hover:bg-brand-dark/90">
            Explore Vendors
          </Button>
          <Button variant="outline" onClick={() => go("/vendors/new")} className="h-auto min-h-11 w-full whitespace-normal">
            Recommend a vendor
          </Button>
          <Button variant="ghost" onClick={() => setOpen(false)} className="h-auto min-h-11 w-full whitespace-normal text-gray-600">
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
